import { WizardField, WizardSection } from "./Wizard";

export type ThreatTo =
  | "none"
  | "party"
  | "president"
  | "mp"
  | "party_member"
  | "branch"
  | "ward"
  | "constituency"
  | "other";

export const THREAT_TO_LABELS: Record<ThreatTo, string> = {
  none: "Not a threat",
  party: "The Party",
  president: "President",
  mp: "MP",
  party_member: "Party member",
  branch: "Branch",
  ward: "Ward",
  constituency: "Constituency",
  other: "Other",
};

const THREAT_OPTIONS: ThreatTo[] = [
  "none", "party", "president", "mp", "party_member",
  "branch", "ward", "constituency", "other",
];

/**
 * Reusable wizard step for tagging a record (case / intel / POI / etc.) as
 * a threat to a protected target. When `threatTo` is "none" the record is
 * excluded from the Threat Detection report. `threatDetails` is encrypted
 * server-side; only the threatTo enum stays plain so we can group.
 */
export function ThreatStepBody({
  threatTo, threatTarget, threatDetails,
  onThreatTo, onThreatTarget, onThreatDetails,
}: {
  threatTo: ThreatTo;
  threatTarget: string;
  threatDetails: string;
  onThreatTo: (v: ThreatTo) => void;
  onThreatTarget: (v: string) => void;
  onThreatDetails: (v: string) => void;
}) {
  const isThreat = threatTo !== "none";
  return (
    <WizardSection
      title="Is this a threat?"
      hint="If this record represents a threat to the party, a leader, or an area, tag it here. Anything tagged shows up on the Threat Detection report; 'Not a threat' keeps it out."
    >
      <WizardField label="Threat to" hint="Pick the closest fit. Use 'Other' if none of the standard targets matches.">
        <select value={threatTo} onChange={(e) => onThreatTo(e.target.value as ThreatTo)}>
          {THREAT_OPTIONS.map((t) => (
            <option key={t} value={t}>{THREAT_TO_LABELS[t]}</option>
          ))}
        </select>
      </WizardField>
      {isThreat && (
        <>
          <WizardField label="Threatened target" hint="Who specifically? e.g. 'Hon. Minister X', 'Branch chair, Mansa Central', 'Ward 7 residents'.">
            <input
              value={threatTarget}
              onChange={(e) => onThreatTarget(e.target.value)}
              placeholder="e.g. President X, Branch Chair Mansa Central"
            />
          </WizardField>
          <WizardField label="Threat details" hint="What's the threat about? Encrypted at rest — only readers in scope can see it.">
            <textarea
              value={threatDetails}
              onChange={(e) => onThreatDetails(e.target.value)}
              rows={4}
              placeholder="What we know about the threat. No speculation."
            />
          </WizardField>
        </>
      )}
    </WizardSection>
  );
}
